import { useQueryClient } from '@tanstack/react-query';
import { clamp } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { userGroupApi } from '@/api';
import { QUERY_KEY_CONST } from '@/constant';
import {
  useCreateUserGroupQuery,
  useMapUserQuery,
  useRemoveUserGroupQuery,
  useUnMapUserQuery,
} from '@/hook';
import {
  ArrowBack,
  ArrowForward,
  CheckBoxOutlineBlank,
  Close,
  Delete,
  Edit,
  PersonAdd,
  SelectCheckBox,
  Trash,
  VButton,
  VDialog__new,
  VFlex,
  VInput,
  VStack,
  VTextButton,
  cn,
} from '@/package';
import { VChip } from '@/package/kiska/src/component/UI/VChip';
import type { User, UserGroup } from '@/type';

import UserRow from '../User/UserRow';

type UserGroupCreateFormType = {
  name: string;
  userList: User[];
};

const UserGroupFormWithTrigger = ({
  editOrCreate,
  editingGroup,
  userGroupList,
  userList,
}: {
  editOrCreate: 'edit' | 'create';
  editingGroup?: UserGroup;
  userGroupList: UserGroup[];
  userList: User[];
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <>
      {editOrCreate === 'create' ? (
        <VButton
          outlined
          size="lg"
          variant="secondary"
          onClick={() => setVisible(true)}
        >
          <PersonAdd />
          {t('userGroup.newGroupButtonText.swedenborgism')}
        </VButton>
      ) : (
        <VTextButton>
          <VFlex items="center" gap={0.25}>
            <Edit className="h-4 w-4" />
            <div
              role="button"
              className="text-body-lg font-bold"
              onClick={() => setVisible(true)}
            >
              {t('user.editGroup.backwoodsman')}
            </div>
          </VFlex>
        </VTextButton>
      )}

      <GroupModal
        editOrCreate={editOrCreate}
        setVisible={setVisible}
        userGroupList={userGroupList}
        userList={userList}
        visible={visible}
        editingGroup={editingGroup}
      />
    </>
  );
};

const GroupModal = ({
  editOrCreate,
  editingGroup,
  userGroupList,
  userList,
  visible,
  setVisible,
}: {
  editOrCreate: 'edit' | 'create';
  editingGroup?: UserGroup;
  userGroupList: UserGroup[];
  userList: User[];
  visible: boolean;
  setVisible: (visible: boolean) => void;
}) => {
  const methods = useForm<UserGroupCreateFormType>({
    defaultValues: editingGroup
      ? { name: editingGroup.name, userList: editingGroup.users ?? [] }
      : {
          name: '',
          userList: [],
        },
  });

  const [step, setStep] = useState(0);

  const setClampedStep = (step: number) => {
    setStep(clamp(step, 0, Funnels.length - 1));
  };

  const onPrevStepClick = () => {
    setClampedStep(step - 1);
  };

  const onNextStepClick = () => {
    setClampedStep(step + 1);
  };

  const Funnels = [
    <GroupNameFunnel
      onNextStepClick={onNextStepClick}
      setVisible={setVisible}
      editOrCreate={editOrCreate}
      editingGroup={editingGroup}
    />,
    <GroupUserFunnel
      editOrCreate={editOrCreate}
      editingGroup={editingGroup}
      onPrevStepClick={onPrevStepClick}
      setVisible={setVisible}
      userList={userList}
      userGroupList={userGroupList}
    />,
  ];

  return (
    <FormProvider {...methods}>
      <VDialog__new visible={visible} onUpdateVisible={setVisible}>
        <VFlex className="h-[657px] w-[1020px] bg-white p-6">
          {Funnels[step]}
        </VFlex>
      </VDialog__new>
    </FormProvider>
  );
};

const GroupNameFunnel = ({
  setVisible,
  onNextStepClick,
  editOrCreate,
  editingGroup,
}: {
  setVisible: (visible: boolean) => void;
  onNextStepClick: () => void;
  editOrCreate: 'edit' | 'create';
  editingGroup?: UserGroup;
}) => {
  const { t } = useTranslation();
  const { register, watch } = useFormContext<UserGroupCreateFormType>();
  const isGroupNameFilled = !!watch('name');
  const [isGroupRemoving, setIsGroupRemoving] = useState(false);
  const isEditing = editOrCreate === 'edit';
  const isRemovable = !!editingGroup && isEditing;

  if (isRemovable && isGroupRemoving)
    return (
      <GroupRemoveFunnel
        cancelRemove={() => setIsGroupRemoving(false)}
        setVisible={setVisible}
        editingGroup={editingGroup}
      />
    );

  return (
    <VStack className="h-full w-full">
      <VFlex items="center" className="w-full" justify="between">
        <VFlex items="center" gap={1}>
          <div className="text-headline-md font-bold text-black">
            {t('user.editGroup.backwoodsman')}
          </div>
          <div className="text-body-xl text-gray-500">Group name</div>
        </VFlex>
        <Close
          className="h-6 w-6 text-gray-400"
          role="button"
          onClick={() => setVisible(false)}
        />
      </VFlex>

      <VFlex className="w-full flex-1" justify="center" items="center">
        <VInput {...register('name')} placeholder="Enter" autoFocus />
      </VFlex>

      <VFlex justify="between" className="w-full">
        <VButton
          className="w-[308px]"
          outlined
          size="md"
          variant="secondary"
          onClick={() => setVisible(false)}
        >
          Cancel
        </VButton>

        {isRemovable && (
          <VFlex
            items="center"
            gap={0.25}
            className="text-error-500"
            role="button"
            onClick={() => setIsGroupRemoving(true)}
          >
            <Trash className="h-4 w-4" />
            <div className="text-body-lg font-bold">Delete group</div>
          </VFlex>
        )}

        <VButton
          size="md"
          className="w-[308px]"
          disabled={!isGroupNameFilled}
          onClick={onNextStepClick}
        >
          <ArrowForward className="h-5 w-5" />
          Continue
        </VButton>
      </VFlex>
    </VStack>
  );
};

const GroupRemoveFunnel = ({
  cancelRemove,
  setVisible,
  editingGroup,
}: {
  setVisible: (visible: boolean) => void;
  cancelRemove: () => void;
  editingGroup: UserGroup;
}) => {
  const { t } = useTranslation();
  const { mutate: removeUserGroup, isPending } = useRemoveUserGroupQuery();
  const onRemoveClick = () => removeUserGroup(editingGroup.id);

  return (
    <VStack className="h-full w-full">
      <VStack gap={1}>
        <VFlex items="center" className="w-full" justify="between">
          <div className="text-headline-md font-bold text-error-500">
            Delete group
          </div>
          <Close
            className="h-6 w-6 text-gray-400"
            role="button"
            onClick={() => setVisible(false)}
          />
        </VFlex>

        <VFlex
          className="gap-[10px]"
          items="center"
          role="button"
          onClick={cancelRemove}
        >
          <ArrowBack className="h-6 w-6" />
          <div>{t('user.editGroup.backwoodsman')}</div>
        </VFlex>
      </VStack>

      <VFlex className="w-full flex-1" justify="center" items="center">
        <VStack gap={0.25}>
          <div className="text-body-xl text-black">
            Are you sure you want to delete this group?
          </div>
          <div className="text-body-lg text-gray-600">
            Deleting the group will permanently remove all members and related
            data.
            <br />
            This action cannot be undone.
          </div>
        </VStack>
      </VFlex>

      <VFlex justify="between" className="w-full">
        <VButton outlined size="md" variant="secondary" className="w-[308px]">
          Cancel
        </VButton>

        <VButton
          size="md"
          className="w-[308px]"
          disabled={isPending}
          onClick={onRemoveClick}
        >
          <Delete className="h-5 w-5" />
          Delete
        </VButton>
      </VFlex>
    </VStack>
  );
};

const GroupUserFunnel = ({
  onPrevStepClick,
  setVisible,
  userList,
  userGroupList,

  editOrCreate,
  editingGroup,
}: {
  onPrevStepClick: () => void;
  setVisible: (visible: boolean) => void;
  userList: User[];
  userGroupList: UserGroup[];

  editOrCreate: 'edit' | 'create';
  editingGroup?: UserGroup;
}) => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<UserGroupCreateFormType>();
  const groupName = watch('name');
  const selectedUserList = watch('userList') ?? [];
  const isAllSelected =
    !!selectedUserList?.length && selectedUserList.length === userList?.length;

  const { mutateAsync: createGroupMutate } = useCreateUserGroupQuery();
  const { mutateAsync: mapUserMutate } = useMapUserQuery();
  const { mutateAsync: unMapUserMutate } = useUnMapUserQuery();
  const queryClient = useQueryClient();

  const removeAllUser = () => setValue('userList', []);
  const addAllUser = () => setValue('userList', userList);

  const isUserSelected = (user: User) =>
    !!selectedUserList.find(u => u.id === user.id);

  const addUser = (user: User) =>
    setValue('userList', [...selectedUserList, user]);
  const removeUser = (user: User) =>
    setValue(
      'userList',
      selectedUserList.filter(u => u.id !== user.id),
    );

  const createGroup = async () => {
    try {
      const {
        data: { resultData },
      } = await createGroupMutate({
        name: groupName,
      });

      const userGroup = resultData;
      await mapUserMutate({ userGroup, users: selectedUserList });
    } finally {
      setVisible(false);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_GROUP_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_KEYS.getList(),
      });
    }
  };

  const updateGroup = async () => {
    if (!editingGroup) return;

    try {
      await userGroupApi.update({ id: editingGroup!.id, name: groupName });
      const toMapUser = selectedUserList.filter(
        su => !editingGroup!.users.find(u => u.id === su.id),
      );
      const toUnMapUser = editingGroup?.users.filter(
        gu => !selectedUserList.find(su => su.id === gu.id),
      );

      await mapUserMutate({
        userGroup: editingGroup,
        users: toMapUser,
      });
      await unMapUserMutate({
        userGroup: editingGroup,
        users: toUnMapUser,
      });
    } finally {
      setVisible(false);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_GROUP_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_KEYS.getList(),
      });
    }
  };

  const onSubmit = () => {
    if (editOrCreate === 'create') createGroup();
    else updateGroup();
  };

  useEffect(() => {
    editingGroup && setValue('userList', editingGroup.users ?? []);
  }, []);

  return (
    <VStack className="h-full w-full">
      <VFlex items="center" className="w-full" justify="between">
        <VFlex items="center" gap={1}>
          <div className="text-headline-md font-bold text-black">
            {t('user.editGroup.backwoodsman')}
          </div>
          <div className="text-body-xl text-gray-500">Members</div>
        </VFlex>
        <Close
          className="h-6 w-6 text-gray-400"
          role="button"
          onClick={() => setVisible(false)}
        />
      </VFlex>

      <VFlex
        className="w-full flex-1 gap-[40px] overflow-hidden pb-4"
        justify="center"
        items="center"
      >
        <VStack gap={0.5} className="h-full flex-1 px-2">
          <VStack className="w-full py-2" gap={1}>
            <VFlex items="center" className="gap-[10px]">
              <ArrowBack
                className="h-6 w-6"
                role="button"
                onClick={onPrevStepClick}
              />
              <div>{groupName}</div>
            </VFlex>
            <div className="w-[296px]">
              <VInput type="search" fluid disabled />
            </div>
          </VStack>

          <div className="flex-1 overflow-hidden">
            <VStack className="h-full overflow-y-auto">
              <button
                className={cn(
                  'flex items-center gap-2 bg-white p-1',
                  'text-body-md text-gray-400',

                  'hover:relative',
                  'hover:after:absolute hover:after:top-0 hover:after:left-0',
                  'hover:after:h-full hover:after:w-full',
                  'hover:after:rounded-md',
                  'hover:after:content-[""]',
                  'hover:after:shadow-sm',
                  'hover:after:z-1',

                  'aria-pressed:bg-skyBlue-200',
                  'disabled:bg-gray-200',
                )}
                aria-pressed={isAllSelected}
                onClick={isAllSelected ? removeAllUser : addAllUser}
              >
                {isAllSelected ? (
                  <SelectCheckBox className={'h-5 w-5'} />
                ) : (
                  <CheckBoxOutlineBlank className={'h-5 w-5'} />
                )}
                <span>All members</span>
              </button>

              {userList.map(user => {
                const isSelected = isUserSelected(user);
                return (
                  <button
                    className={cn(
                      'flex items-center gap-2 bg-white p-1',

                      'hover:relative',
                      'hover:after:absolute hover:after:top-0 hover:after:left-0',
                      'hover:after:h-full hover:after:w-full',
                      'hover:after:rounded-md',
                      'hover:after:content-[""]',
                      'hover:after:shadow-sm',
                      'hover:after:z-1',

                      'enabled:active:bg-skyBlue-200',
                      'aria-pressed:bg-skyBlue-200',
                      'disabled:bg-gray-200',
                    )}
                    aria-pressed={isSelected}
                    onClick={() => {
                      isSelected ? removeUser(user) : addUser(user);
                    }}
                    key={user.id}
                  >
                    {isSelected ? (
                      <SelectCheckBox className={'h-5 w-5'} />
                    ) : (
                      <CheckBoxOutlineBlank className={'h-5 w-5'} />
                    )}
                    <UserRow user={user} userGroups={userGroupList} />
                  </button>
                );
              })}
            </VStack>
          </div>
        </VStack>

        <VStack
          className="h-full w-[308px] rounded-md border border-gray-300"
          gap={1}
        >
          <VStack className="h-full overflow-hidden px-2" gap={1}>
            <div className="text-headline-sm text-blue-300">Added Members</div>

            <div className="flex-1 overflow-y-auto">
              <VFlex wrap="wrap" gap={0.25}>
                {selectedUserList.map(user => {
                  return (
                    <VChip key={user.id} onDelete={() => removeUser(user)}>
                      {user.email}
                    </VChip>
                  );
                })}
              </VFlex>
            </div>
          </VStack>
        </VStack>
      </VFlex>

      <VFlex justify="between" className="w-full">
        <VButton outlined size="md" variant="secondary" className="w-[308px]">
          Cancel
        </VButton>

        <VButton
          size="md"
          className="w-[308px]"
          onClick={() => {
            onSubmit();
          }}
        >
          {editOrCreate === 'create' ? 'Create' : 'Edit'}
        </VButton>
      </VFlex>
    </VStack>
  );
};

export default UserGroupFormWithTrigger;
